/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { Code, Globe, Brain, Cpu, Zap, Users, ShieldCheck } from 'lucide-react';
import { Roadmap } from '../components/Roadmap';
import { LogoIcon } from '../components/logo-icon';

interface AboutProps {
    onBack: () => void;
}

const PARTNERS = [
    { name: 'Solana', logo: 'https://cryptologos.cc/logos/solana-sol-logo.png' },
    { name: 'Sui', logo: 'https://cryptologos.cc/logos/sui-sui-logo.png' },
    { name: 'Helius', svg: <svg viewBox="0 0 22 24" fill="currentColor" className="w-full h-full text-[#FE5B29]"><path d="M11.5303 0.222656L3.92188 12.2227H10.5003L7.90029 23.7773L15.5007 11.7773H8.9303L11.5303 0.222656Z" /></svg> },
    { name: 'IPFS', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/1/18/Ipfs-logo-1024-ice-text.png/1024px-Ipfs-logo-1024-ice-text.png' },
    { name: 'Jupiter', logo: 'https://static.jup.ag/jup/icon.png' },
    { name: 'Raydium', logo: 'https://cryptologos.cc/logos/raydium-ray-logo.png' },
    { name: 'Pyth', logo: 'https://cryptologos.cc/logos/pyth-network-pyth-logo.png' },
];

export const About: React.FC<AboutProps> = ({ onBack }) => {
    return (
        <div className="min-h-screen bg-[#F9F8F4] flex flex-col relative animate-fade-in">
            <main className="flex-1 container mx-auto px-6 pb-24">
                
                {/* Hero */}
                <div className="text-center max-w-4xl mx-auto relative pt-16 pb-20">
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-nobel-gold/10 rounded-full blur-[120px] pointer-events-none"></div>

                    <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white border border-stone-200 text-stone-500 text-xs font-bold tracking-widest uppercase rounded-full mb-8 shadow-sm">
                        <LogoIcon className="w-4 h-4" variant="default" />
                        The Foundry Vision
                    </div>
                    
                    <h1 className="font-serif text-5xl md:text-7xl text-stone-900 mb-8 leading-[1.1]">
                        Forging the Future of <br/>
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-nobel-gold via-amber-600 to-amber-700">Digital Assets</span>
                    </h1>
                    
                    <p className="text-xl text-stone-500 leading-relaxed max-w-2xl mx-auto font-light">
                        We are building the definitive infrastructure for the high-velocity token economy. 
                        Where AI intelligence meets instant liquidity on Solana & Sui.
                    </p>
                </div>
                
                {/* Story Section - Glassmorphism */}
                <div className="max-w-6xl mx-auto bg-white/80 backdrop-blur-md p-8 md:p-12 rounded-3xl border border-stone-200 shadow-soft-xl mb-24 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-stone-100 to-stone-50 rounded-bl-[100px] -z-10 opacity-50"></div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                        <div>
                            <h3 className="font-serif text-3xl text-stone-900 mb-6">Our Manifesto</h3>
                            <div className="prose prose-stone prose-lg text-stone-600 leading-relaxed">
                                <p>
                                    The token economy is evolving faster than the tools built to support it. Creators face complex barriers, while traders navigate a sea of noise.
                                </p>
                                <p>
                                    <strong>The Foundry</strong> was born from a simple idea: What if launching a token was as easy as sending an email, and trading was guided by collective intelligence?
                                </p>
                                <p>
                                    We combine <strong>No-Code Smart Contracts</strong> with our proprietary <strong>Swarm Neural Engine</strong> to create a vertically integrated ecosystem where safety, speed, and liquidity are standard, not optional.
                                </p>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="p-6 bg-stone-50 rounded-2xl border border-stone-100">
                                <div className="text-3xl font-bold text-stone-900 mb-1">200K+</div>
                                <div className="text-xs font-bold text-stone-400 uppercase tracking-wider">Transactions Analyzed</div>
                            </div>
                            <div className="p-6 bg-stone-50 rounded-2xl border border-stone-100">
                                <div className="text-3xl font-bold text-stone-900 mb-1">$85M+</div>
                                <div className="text-xs font-bold text-stone-400 uppercase tracking-wider">Volume Processed</div>
                            </div>
                            <div className="p-6 bg-stone-900 text-white rounded-2xl border border-stone-800 col-span-2 flex items-center justify-between">
                                <div>
                                    <div className="text-3xl font-bold text-nobel-gold mb-1">0.5s</div>
                                    <div className="text-xs font-bold text-stone-400 uppercase tracking-wider">Execution Latency</div>
                                </div>
                                <Zap size={32} className="text-nobel-gold" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Core Pillars */}
                <div className="mb-24">
                    <div className="text-center mb-12">
                        <h3 className="font-serif text-3xl text-stone-900">The Technology Stack</h3>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[
                            {
                                icon: <Cpu size={24} />,
                                title: "Multi-VM Foundry",
                                desc: "Unified deployment pipeline for Solana (SVM) and Sui (MoveVM). Handles Token-2022 extensions, metadata pointer logic, and Move packages automatically."
                            },
                            {
                                icon: <Brain size={24} />,
                                title: "Neural Consensus",
                                desc: "Our AI engine doesn't just look at price. It runs hundreds of simulation nodes to analyze mempool pressure, wallet clusters, and social sentiment."
                            },
                            {
                                icon: <ShieldCheck size={24} />,
                                title: "Liquidity Guard",
                                desc: "Automated bonding curves ensure every launch has immediate liquidity. Rug-pull vectors like unverified mint authorities are flagged instantly."
                            }
                        ].map((item, i) => (
                            <div key={i} className="group bg-white p-8 rounded-2xl border border-stone-200 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 relative overflow-hidden">
                                <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                                    {item.icon}
                                </div>
                                <div className="w-12 h-12 bg-stone-50 rounded-xl flex items-center justify-center mb-6 text-stone-900 group-hover:bg-stone-900 group-hover:text-nobel-gold transition-colors">
                                    {item.icon}
                                </div>
                                <h3 className="font-serif text-xl text-stone-900 mb-3 font-bold">{item.title}</h3>
                                <p className="text-stone-500 text-sm leading-relaxed">
                                    {item.desc}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Roadmap Section */}
                <div className="mb-24">
                    <Roadmap />
                </div>

                {/* Partners / Powered By */}
                <div className="py-16 bg-white rounded-3xl border border-stone-200 text-center">
                    <p className="text-xs font-bold text-stone-400 uppercase tracking-[0.2em] mb-12">Powered By Industry Leaders</p>
                    <div className="flex flex-wrap justify-center gap-12 lg:gap-16 items-center px-6 grayscale hover:grayscale-0 transition-all duration-500">
                        {PARTNERS.map(p => (
                            <div key={p.name} className="h-8 w-auto opacity-60 hover:opacity-100 transition-opacity" title={p.name}>
                                {p.logo ? (
                                    <img src={p.logo} alt={p.name} className="h-full object-contain" />
                                ) : (
                                    <div className="h-8 w-8">{p.svg}</div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

            </main>
        </div>
    );
};